/**
 * Format partial dates (YYYY, YYYY-MM, YYYY-MM-DD) to locale-aware, human-readable format.
 * Handles Discogs-style "00" for unknown month/day (e.g. "2006-00-00" → "2006").
 *
 * @param date - Date string (YYYY, YYYY-MM, or YYYY-MM-DD — month/day may be "00")
 * @param locale - BCP 47 locale string (e.g. "en", "pt", "es")
 * @returns Formatted date string
 *
 * @example
 * formatReleaseDate("2023", "en") // "2023"
 * formatReleaseDate("2023-03", "en") // "Mar 2023"
 * formatReleaseDate("2023-03-15", "en") // "Mar 15, 2023"
 * formatReleaseDate("2006-00-00", "en") // "2006"
 * formatReleaseDate("2023-03-00", "pt") // "mar. de 2023"
 * formatReleaseDate(null, "en") // ""
 */
export function formatReleaseDate(date: string | null | undefined, locale: string): string {
  if (!date) return "";

  const parts = date.split("-");
  const year = parseInt(parts[0], 10);
  if (!year) return "";

  const month = parts[1] ? parseInt(parts[1], 10) : 0;
  const day = parts[2] ? parseInt(parts[2], 10) : 0;

  // Unknown month → year only: "2006-00-00" → "2006"
  if (!month) return String(year);

  // Unknown day → month + year: "2023-03-00" → "Mar 2023"
  if (!day) {
    return new Intl.DateTimeFormat(locale, { year: "numeric", month: "short" }).format(
      new Date(year, month - 1),
    );
  }

  // Full date: "2023-03-15" → "Mar 15, 2023"
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(year, month - 1, day));
}
